import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

export default function AboutPage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://documogul.com';
    
    document.title = '关于我们 - 星允信息技术有限公司 | 专业PDF解决方案提供商';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '星允信息技术有限公司是一家专注于文档智能处理技术的创新企业，致力于为全球客户提供最先进的PDF处理解决方案。我们与Datalogics建立战略合作关系，成为中国区独家技术合作伙伴。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '星允信息,关于我们,公司介绍,企业文化,发展历程,PDF解决方案,Datalogics合作伙伴');
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/about`);

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "关于星允信息",
      "description": "星允信息技术有限公司是一家专注于文档智能处理技术的创新企业",
      "url": `${siteUrl}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "星允信息技术有限公司",
        "foundingDate": "2018",
        "description": "专注于文档智能处理技术的创新企业，致力于为全球客户提供最先进的PDF处理解决方案"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const milestones = [
    {
      year: '2018',
      title: '公司成立',
      description: '星允信息技术有限公司正式成立，专注于文档智能处理技术。'
    },
    {
      year: '2019',
      title: '技术突破',
      description: '成功开发出第一代PDF处理引擎，获得多项技术专利。'
    },
    {
      year: '2021',
      title: '市场拓展',
      description: '产品正式商业化，服务超过100家企业客户。'
    },
    {
      year: '2025',
      title: '国际合作',
      description: '与Datalogics建立战略合作关系，成为中国区独家技术合作伙伴。'
    }
  ];

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: '创新驱动',
      description: '持续投入研发，推动文档处理技术的创新与发展。',
      color: 'bg-teal-500'
    },
    {
      icon: 'ri-user-heart-line',
      title: '客户至上',
      description: '以客户需求为导向，提供最优质的产品和服务。',
      color: 'bg-cyan-500'
    },
    {
      icon: 'ri-shield-check-line',
      title: '品质保证',
      description: '严格的质量控制体系，确保产品的稳定性和可靠性。',
      color: 'bg-teal-600'
    },
    {
      icon: 'ri-team-line',
      title: '团队协作',
      description: '倡导开放合作的团队文化，共同创造卓越成果。',
      color: 'bg-cyan-600'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-teal-900 to-gray-900 text-white py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/2bdec4b36779e40131c24cccabdf6d23.jpg')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6" style={{ marginTop: '15px' }}>
              关于<span className="text-teal-400">星允信息</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed">
              我们是一家专注于文档智能处理技术的创新企业，致力于为全球客户提供最先进的PDF处理解决方案。
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contact" 
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                联系我们
              </Link>
              <Link 
                to="/careers" 
                className="border border-white hover:bg-white hover:text-gray-900 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                加入我们
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">我们的使命</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                通过创新的文档智能技术，帮助企业提升工作效率，降低运营成本，实现数字化转型。我们相信技术的力量能够改变传统的文档处理方式，为客户创造更大的价值。
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                我们致力于成为全球领先的文档智能解决方案提供商，推动行业标准的建立和发展。
              </p>
            </div>
            <div className="relative w-full h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20office%20team%20collaboration%20scene%2C%20diverse%20Chinese%20professionals%20working%20together%20on%20documents%20and%20technology%20in%20meeting%20room%2C%20clean%20corporate%20environment%20with%20teal%20and%20cyan%20lighting%20accents%2C%20teamwork%20and%20innovation%20atmosphere%2C%20professional%20business%20photography%2C%20digital%20document%20workflow&width=600&height=400&seq=mission-teal-1&orientation=landscape"
                alt="团队协作"
                className="rounded-lg shadow-lg object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">核心价值观</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              我们的价值观指导着我们的每一个决策和行动，塑造着我们的企业文化。
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group hover:transform hover:scale-105 transition-all duration-300">
                <div className={`w-16 h-16 ${value.color} rounded-full flex items-center justify-center mx-auto mb-6 group-hover:shadow-lg transition-shadow`}>
                  <i className={`${value.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">发展历程</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              从初创到成长，我们一步步走向成功，每个里程碑都见证着我们的进步。
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-teal-200"></div>
            
            {milestones.map((milestone, index) => (
              <div key={index} className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow border border-teal-100">
                    <div className="text-2xl font-bold text-teal-600 mb-2">{milestone.year}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{milestone.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                  </div>
                </div>
                
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full border-4 border-white shadow-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">准备开始您的数字化转型之旅？</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            联系我们，了解如何通过我们的文档智能解决方案提升您的业务效率。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/contact" 
              className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              联系我们
            </Link>
            <Link 
              to="/products" 
              className="border border-white hover:bg-white hover:text-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              查看产品
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
